import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface ContactVerificationModalProps {
  contactId: number;
  contactName: string;
  onSubmit: (code: string) => Promise<void>;
  onCancel: () => void;
  onResend: () => Promise<void>;
  canResend: boolean;
  nextAllowedAt?: Date;
}

export function ContactVerificationModal({
  contactName,
  onSubmit,
  onCancel,
  onResend,
  canResend,
  nextAllowedAt,
}: ContactVerificationModalProps) {
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResending, setIsResending] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!/^\d{6}$/.test(code)) {
      setError('Code must be 6 digits');
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(code);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResend = async () => {
    setError(null);
    setIsResending(true);
    try {
      await onResend();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to resend code');
    } finally {
      setIsResending(false);
    }
  };

  const formatTime = (date: Date) => {
    return new Date(date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <h2 className="text-xl font-semibold mb-4">Verify Contact</h2>
        <p className="text-gray-600 mb-4">
          Enter the 6-digit verification code from the verification message for {contactName}
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="code">Verification Code</Label>
            <Input
              id="code"
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              maxLength={6}
              disabled={isSubmitting}
              autoComplete="one-time-code"
              className="text-center text-2xl tracking-widest font-mono"
              required
            />
          </div>

          {error && <div className="text-sm text-red-600">{error}</div>}

          <div className="flex justify-between items-center">
            <div>
              {!canResend && nextAllowedAt && (
                <p className="text-sm text-gray-500">Resend at {formatTime(nextAllowedAt)}</p>
              )}
              {canResend && (
                <Button
                  type="button"
                  variant="link"
                  onClick={handleResend}
                  disabled={isResending}
                  className="p-0"
                >
                  {isResending ? 'Resending...' : 'Resend Code'}
                </Button>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting || code.length !== 6}>
              {isSubmitting ? 'Verifying...' : 'Verify'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
